import React, { useState } from "react";
import { useSelector } from "react-redux";
import StudentTable from "../shared/components/Table/StudentTable";
import CalendarComponent from "../shared/components/CalendarComponent/CalendarComponent";
import Pagination from "../shared/components/Pagination/Pagination";
import reset_icon from "../assets/images/reset_icon.svg";
import useExploreQueries from "../store/explore/useExploreQueries";
import { formatDateDDMMYYTime } from "../shared/utils/formatDate";
import { PERMISSION_TYPES } from "../shared/utils/permissions";
import { checkPermission } from "../shared/utils/permissions";
import { getAccountType } from "../store/auth/selectors";
import loader from "../assets/gifs/loader.gif";
import "./ExplorePage.css";

const ExplorePage = () => {
  const [currPage, setCurrPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [stuData, setStuData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [registerDate, setRegisterDate] = useState({
    date_range_start: null,
    date_range_end: null,
  });
  const [eventDate, setEventDate] = useState({
    date_range_start: null,
    date_range_end: null,
  });
  const [activeFilter, setActiveFilter] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPayload, setCurrentPayload] = useState(null);

  const { loading, error, fetchStudentsData, exportData } = useExploreQueries();

  const accountType = useSelector(getAccountType);
  const checkUserPermission = (permissionType) =>
    checkPermission(accountType, permissionType);
  const canExportData = checkUserPermission(
    PERMISSION_TYPES.EXPORT_EXPLORE_STUDENT_DATA
  );

  const columns = [
    { key: "schoolId", label: "SCHOOL ID" },
    { key: "crnId", label: "CRN ID" },
    { key: "name", label: "NAME" },
    { key: "eventName", label: "EVENT NAME" },
    { key: "level", label: "LEVEL" },
    { key: "round", label: "ROUND" },
    { key: "eventDate", label: "EVENT DATE" },
    { key: "phone", label: "PHONE" },
    { key: "grade", label: "GRADE" },
    { key: "loginId", label: "LOGIN ID" },
    { key: "regSource", label: "REG. SOURCE" },
    { key: "registeredOn", label: "REGISTERED ON" },
    { key: "attempt", label: "ATTEMPT" },
  ];

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getExportFileName = () => {
    const currentDate = formatDate(new Date());
    const filterType = activeFilter === "register" ? "registration" : "event";
    const dateRange =
      activeFilter === "register"
        ? `${formatDate(registerDate.date_range_start)}_to_${formatDate(
            registerDate.date_range_end
          )}`
        : `${formatDate(eventDate.date_range_start)}_to_${formatDate(
            eventDate.date_range_end
          )}`;

    return `explore_${filterType}_${dateRange}_exported_${currentDate}.csv`;
  };

  const handleExportCSV = async () => {
    if (!canExportData) return;

    try {
      const response = await exportData(currentPayload);
      if (response.export_url) {
        window.open(response.export_url, "_blank");
      }
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  const createPayload = () => {
    return {
      registration_date:
        registerDate.date_range_start && registerDate.date_range_end
          ? {
              date_range_start: `${formatDate(
                registerDate.date_range_start
              )}T00:00`,
              date_range_end: `${formatDate(
                registerDate.date_range_end
              )}T23:59`,
            }
          : null,
      event_date:
        eventDate.date_range_start && eventDate.date_range_end
          ? {
              date_range_start: `${formatDate(
                eventDate.date_range_start
              )}T00:00`,
              date_range_end: `${formatDate(eventDate.date_range_end)}T23:59`,
            }
          : null,
    };
  };

  const fetchData = async (page, itemsPerPage, payload) => {
    try {
      const response = await fetchStudentsData(page, itemsPerPage, payload);
      const mappedData = response.hits.hits.map((hit) => ({
        id: hit._id,
        schoolId: hit._source.student_details?.school_id || "",
        crnId: hit._source.crn_id || "",
        name: `${hit._source.student_details?.first_name || ""} ${
          hit._source.student_details?.last_name || ""
        }`.trim(),
        eventName: hit._source.event_details?.name || "",
        level: hit._source.event_details?.level || "",
        round: hit._source.event_details?.round || "",
        eventDate: formatDateDDMMYYTime(
          hit._source.event_details?.start_date_time
        ),
        phone: hit._source.student_details?.phone || "",
        grade: hit._source.student_details?.grade_id || "",
        loginId: hit._source.student_details?.admission_id || "",
        regSource: hit._source.student_details?.registration_source || "",
        registeredOn: formatDateDDMMYYTime(
          hit._source.student_details?.registration_date
        ),
        attempt: hit._source.exam_attempted ? "Yes" : "No",
      }));
      setStuData(mappedData);
      setTotalCount(response.hits.total.value);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const handleGetData = async () => {
    setHasSearched(true);
    const payload = createPayload();
    setCurrentPayload(payload);
    await fetchData(currPage, limit, payload);
  };

  const handleResetFilters = () => {
    setRegisterDate({
      date_range_start: null,
      date_range_end: null,
    });
    setEventDate({
      date_range_start: null,
      date_range_end: null,
    });
    setActiveFilter(null);
    setStuData([]);
    setTotalCount(0);
    setHasSearched(false);
    setCurrPage(1);
    setCurrentPayload(null);
  };

  const handleRegisterDateChange = (type, date) => {
    if (date && type === "start") {
      setActiveFilter("register");
    }
    setRegisterDate((prev) => ({
      ...prev,
      [type === "start" ? "date_range_start" : "date_range_end"]: date,
    }));
  };

  const handleEventDateChange = (type, date) => {
    if (date && type === "start") {
      setActiveFilter("event");
    }
    setEventDate((prev) => ({
      ...prev,
      [type === "start" ? "date_range_start" : "date_range_end"]: date,
    }));
  };

  const handlePageChange = async (newPage) => {
    if (hasSearched && currentPayload) {
      await fetchData(newPage, limit, currentPayload);
    }
    setCurrPage(newPage);
  };

  const handleLimitChange = async (newLimit) => {
    if (hasSearched && currentPayload) {
      await fetchData(1, newLimit, currentPayload);
    }
    setLimit(newLimit);
    setCurrPage(1);
  };

  const isGetDataEnabled = () => {
    return (
      (registerDate.date_range_start && registerDate.date_range_end) ||
      (eventDate.date_range_start && eventDate.date_range_end)
    );
  };

  if (loading) {
    return (
      <div className="loaderCont">
        <img
          src={loader}
          alt="Loading spinner"
          style={{ width: "200px", height: "200px", margin: "auto" }}
        />
      </div>
    );
  }

  return (
    <div className="explore-mainCont">
      <div className="titlecont">
        <h3>Explore</h3>
        <p>Manage your explore</p>
      </div>
      <div className="filters-wrapper">
        <div className="explore-box">
          <p>Registration Date</p>
          <CalendarComponent
            onStartDateChange={(date) =>
              handleRegisterDateChange("start", date)
            }
            onEndDateChange={(date) => handleRegisterDateChange("end", date)}
            startDate={registerDate.date_range_start}
            endDate={registerDate.date_range_end}
            disabled={activeFilter === "event"}
            type="registration"
          />
        </div>
        <div className="explore-box">
          <p>Event Date</p>
          <CalendarComponent
            onStartDateChange={(date) => handleEventDateChange("start", date)}
            onEndDateChange={(date) => handleEventDateChange("end", date)}
            startDate={eventDate.date_range_start}
            endDate={eventDate.date_range_end}
            disabled={activeFilter === "register"}
            type="event"
          />
        </div>
        <button
          className={`get-data-btn-explore ${
            !isGetDataEnabled() ? "disabled" : ""
          }`}
          onClick={handleGetData}
          disabled={!isGetDataEnabled()}
        >
          Get Data
        </button>
        <button
          onClick={handleResetFilters}
          className={`planner-reset-button-explore`}
        >
          <img src={reset_icon} alt="Reset" />
          Reset Filters
        </button>
        {stuData && stuData.length > 0 && canExportData && (
          <button onClick={handleExportCSV} className="export-btn-explore">
            Export Data
          </button>
        )}
      </div>

      <div className="tableCont">
        {stuData && stuData.length > 0 ? (
          <>
            <div className="students-info">
              <span style={{ fontWeight: "500" }}>
                Total Students: {totalCount}
                {totalCount >= 10000 ? "+" : ""}
              </span>
              <div className="table-controls">
                <Pagination
                  totalItems={totalCount}
                  currentPage={currPage}
                  itemsPerPage={limit}
                  itemsPerPageOptions={[100, 200, 500, 1000, 2000]}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleLimitChange}
                />
              </div>
            </div>
            <StudentTable
              data={stuData}
              columns={columns}
              showRowSelector={false}
            />
          </>
        ) : hasSearched ? (
          <div className="error-message-explore">No results found</div>
        ) : (
          <div className="no-data-message-explore">
            Please select a date range
          </div>
        )}
      </div>
    </div>
  );
};

export default ExplorePage;
